import { AfterViewInit, ChangeDetectionStrategy, Component, ElementRef, HostBinding, OnDestroy, ViewChild } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import {
  DocumentInitParameters,
  OnProgressParameters,
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
  PDFPageProxy,
} from 'pdfjs-dist/types/src/display/api';
import { BasePreviewComponent } from '../base-preview/base-preview.component';
import { ErrorType } from '@shared/services/stream-content.service';

const pdfJsLib = require('pdfjs-dist/build/pdf.js');
const pdfJsWorker = require('pdfjs-dist/build/pdf.worker.entry.js');
pdfJsLib.GlobalWorkerOptions.workerSrc = pdfJsWorker;

@UntilDestroy()
@Component({
  selector: 'pdf-view',
  templateUrl: './pdf-preview.component.html',
  styleUrls: ['./pdf-preview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PdfPreviewComponent extends BasePreviewComponent implements AfterViewInit, OnDestroy {
  @HostBinding('style.display')
  get display() {
    if (this.show) {
      this.totalPages.emit(this._totalPages);
      this.currentPage.emit(this._currentPage);
    }
    return this.show ? 'flex' : 'none';
  }

  @HostBinding('style.margin')
  get margin() {
    if (this.previewMode === 'popup') return 0;
  }

  @ViewChild('viewPort', { read: ElementRef }) viewPort: ElementRef;
  @ViewChild('loadingRef') loadingRef: ElementRef;

  private intersectionObserver: IntersectionObserver;
  private firstPage: boolean;
  private _currentPage: number;
  private _totalPages: number;
  private pdfLoadingTask: PDFDocumentLoadingTask;
  private pdfInstance: PDFDocumentProxy;
  private srcUrl: string;
  pages: { placeholderId: string; canvas: HTMLCanvasElement }[] = [];
  progressPercentages = 0;
  showProgressBar = false;

  async ngAfterViewInit() {
    this.srcUrl = this.model.item.srcUrl;
    if (!this.modelValidated()) {
      this.abort();
      return;
    }
    this.renderPdf();

    this.intersectionObserver = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          const id = entry.target.attributes['id'].value;
          this._currentPage = +id.split('-')[1];
          this.currentPage.emit(this._currentPage);
          this.placeCanvas(id);
          this.cdr.markForCheck();
        }
      },
      { threshold: 0.25 }
    );
  }

  private renderPdf() {
    this.loaded = false;
    this.totalPages.emit(0);
    this.showProgressBar = true;
    this.firstPage = true;
    const req: DocumentInitParameters = {
      url: this.srcUrl,
      withCredentials: true,
      httpHeaders: { Accept: 'application/pdf', Authorization: `Bearer ${this.token}` },
    };
    this.pdfLoadingTask = pdfJsLib.getDocument(req);

    this.pdfLoadingTask.onPassword = () => {
      this.errorType = 'PasswordException';
      this.loaded = true;
      this.cdr.markForCheck();
    };
    this.pdfLoadingTask.onProgress = (progress: OnProgressParameters) => {
      this.progressPercentages = Math.trunc((progress.loaded / progress.total) * 100);
      if (this.loadingRef) {
        this.loadingRef.nativeElement.style.backgroundImage = `linear-gradient(90deg, var(--color-primary) ${this.progressPercentages}%, transparent 0%)`;
      }
      this.cdr.markForCheck();
    };
    this.pdfLoadingTask.promise
      .then(
        (pdf: PDFDocumentProxy) => this.onSuccess(pdf),
        (reason) => {
          throw reason;
        }
      )
      .catch((e) => {
        this.totalPages.emit(0);
        if (e.status === 500 || e.name === 'InvalidPDFException') {
          e = 'loadingError' as ErrorType;
        } else if (e.status === 401) {
          e = 'unEnablePreview' as ErrorType;
        }
        this.errorType = e;
        this.loaded = true;
      })
      .finally(() => {
        this.cdr.markForCheck();
      });
  }

  private onSuccess(pdf: PDFDocumentProxy) {
    this.pdfInstance = pdf;
    this._totalPages = this.pdfInstance.numPages;

    for (let pageNum = 1; pageNum <= this.pdfInstance.numPages; pageNum++) {
      this.appendPlaceholder(`placeholder-${pageNum}`);
    }
    this.intersectPlaceholders();
  }

  private async renderPage(currPage: PDFPageProxy, pageNum: number): Promise<HTMLCanvasElement> {
    let pdfViewport = currPage.getViewport({ scale: 3, dontFlip: false });
    const canvas = document.createElement('canvas');
    canvas.style.marginBottom = '16px';
    canvas.style.width = '100%';
    canvas.style.borderRadius = '5px';
    canvas.id = `page-${pageNum}`;
    const context = canvas.getContext('2d');
    canvas.height = pdfViewport.height;
    canvas.width = pdfViewport.width;

    try {
      await currPage.render({ canvasContext: context, viewport: pdfViewport });
      this.showProgressBar = false;
      this.cdr.markForCheck();
    } catch (e) {
      this.errorType = e;
      this.loaded = true;
    }
    if (this.firstPage) {
      this.loaded = true;
      this.firstPage = false;
      this.cdr.markForCheck();
    }
    return canvas;
  }

  private intersectPlaceholders() {
    [...this.viewPort.nativeElement.childNodes]
      .filter((c) => c.id?.startsWith('placeholder'))
      .forEach((c) => {
        this.intersectionObserver.observe(c);
      });
  }

  private async placeCanvas(id: string) {
    const pageEl = [...this.viewportEl.childNodes].find((c) => c['id'] === id) as HTMLDivElement;
    if (!pageEl || pageEl.id.startsWith('page')) return;
    const pageNum = +id.split('-')[1];
    const page = await this.pdfInstance.getPage(pageNum);
    const canvas = await this.renderPage(page, pageNum);
    this.viewportEl.insertBefore(canvas, pageEl);
    this.viewportEl.removeChild(pageEl);
    this.intersectionObserver.observe(canvas);
    this.intersectionObserver.unobserve(pageEl);
    this.cdr.markForCheck();
  }

  private appendPlaceholder(id: string) {
    const placeholder = document.createElement('div');
    placeholder.id = id;
    placeholder.style.width = '100%';
    placeholder.style.height = '100%';
    placeholder.style.borderRadius = '5px';
    placeholder.style.backgroundColor = 'var(--color-bg)';
    this.viewportEl.appendChild(placeholder);
  }

  set prevWidth(val: number) {
    this._width = val;
    this.scaleEl(this.viewPort?.nativeElement, val);
  }

  get prevWidth() {
    return this._width;
  }

  get viewportEl(): HTMLDivElement {
    return this.viewPort.nativeElement;
  }

  private abort() {
    this.errorType = 'loadingError';
    this.loaded = true;
    this.cdr.markForCheck();
  }

  ngOnDestroy(): void {
    this.pdfLoadingTask?.destroy();
  }
}
